// SPDX-License-Identifier: Apache-2.0

// seed-demo.ts — pre-built connected vault for the empty-state "try demo data" CTA.
// 20 docs forming a real graph via [[wikilinks]] — projects, experiments, people,
// meetings, daily journal, papers, concepts, tools, an "areas/health" private note
// (so the user can see Veil's tier badge in chat), and one archived spec.
// All frontmatter is real (sensitivity tier, tags, type, permalink).

import { getVaultStore, type VaultStore } from "./vault-store";
import type { VaultFrontmatter, Tier, IngestResult } from "./schema";

interface DemoSeed {
  path: string;
  front: VaultFrontmatter;
  body: string;
}

function front(
  title: string,
  type: VaultFrontmatter["type"],
  tags: string[],
  permalink: string,
  sensitivity: Tier = "internal",
): VaultFrontmatter {
  return {
    title,
    type,
    tags,
    permalink,
    sensitivity,
    created: "2026-04-26",
  };
}

const DEMO: DemoSeed[] = [
  // ── projects ────────────────────────────────────────────────────────
  {
    path: "projects/tether-brain.md",
    front: front("Tether Brain v2", "project", ["project", "wgsl", "local-first"], "tether-brain"),
    body:
      "Clean rewrite of Tether at tether.dev — one URL, zero install, vault + chat + temple in one tab.\n\n" +
      "Built on [[zero-tvm]] for the secret-tier backend, [[ternary-veil]] for privacy routing, and a hand-written WGSL kernel set ported from the OLD repo.\n\n" +
      "See [[E29-veil-cohort]] for the cohort-blender evaluation and [[E30-zero-tvm-bench]] for the engine benchmark.\n\n" +
      "Owner: [[baris]].",
  },
  {
    path: "projects/zero-tvm.md",
    front: front("Zero-TVM", "project", ["project", "llm-inference", "webgpu", "flagship"], "zero-tvm"),
    body:
      "Phi-3-mini in 10 hand-written WGSL kernels. No TVM, no WebLLM runtime, no compiler.\n\n" +
      "157 KB JS bundle vs WebLLM's 5.9 MB. 42.14 tok/s on M2 Pro — 22% behind WebLLM, honest.\n\n" +
      "The point isn't speed. It's auditability — every FLOP the model executes is in a file you can open.\n\n" +
      "See [[wgsl-compute]] for the kernel-set thesis and [[E30-zero-tvm-bench]] for the head-to-head numbers.\n\n" +
      "Lives at github.com/abgnydn/zero-tvm. Used by [[tether-brain]] as the secret-tier chat backend.",
  },
  {
    path: "projects/ternary-veil.md",
    front: front("Ternary Veil", "project", ["project", "privacy", "k-anon", "rust"], "ternary-veil"),
    body:
      "Privacy layer for [[tether-brain]]: classifies content by sensitivity tier (public / internal / private / secret), routes accordingly.\n\n" +
      "For private content, applies [[k-anon-cohort]] sampling — the real note is hidden in K plausible decoys before any remote LLM call.\n\n" +
      "Phase 8 (k-anon cohort sampler) is the novel contribution — see [[E29-veil-cohort]] for the threat model + caveats and [[E31-cohort-eval]] for empirical numbers.\n\n" +
      "secret-tier content never leaves the machine — routes to a local model only.",
  },

  // ── experiments ─────────────────────────────────────────────────────
  {
    path: "research-vault/experiments/E29-veil-cohort.md",
    front: front("E29 — Veil Cohort Sampler", "experiment", ["experiment", "veil", "k-anon"], "e29-veil-cohort"),
    body:
      "_2026-04-12 → 2026-04-23._\n\n" +
      "Hypothesis: a K-anonymous cohort sampler over a vault's semantic neighbors is enough to make remote LLM calls indistinguishable for the prompt-leakage threat model, with K=8.\n\n" +
      "Method: embed query, take K-1 nearest neighbors via [[cosine-similarity]] over [[multilingual-e5-small]], interleave with the real note, send the cohort.\n\n" +
      "Result: ✓ for the documented threat model. Caveats: streaming side-channels, request-time jitter, pool-range fingerprint. See [[k-anon-cohort]] for the formal write-up.\n\n" +
      "Used by [[ternary-veil]] in private-tier routing inside [[tether-brain]].",
  },
  {
    path: "research-vault/experiments/E30-zero-tvm-bench.md",
    front: front("E30 — Zero-TVM head-to-head", "experiment", ["experiment", "zero-tvm", "wgsl"], "e30-zero-tvm-bench"),
    body:
      "_2026-04-18._\n\n" +
      "Same Phi-3-mini-q4f16_1 weights, same M2 Pro, same prompt, greedy decoding.\n\n" +
      "WebLLM 0.2.80: 51.5 tok/s decode. [[zero-tvm]]: 42.14 tok/s decode. 22% gap.\n\n" +
      "Wins for [[zero-tvm]]: 8.4× fewer kernels (10 vs 85), 4.2× fewer WGSL lines (3,078 vs 12,962), 38× smaller JS bundle (157 KB vs 5.9 MB), no compiler.\n\n" +
      "Conclusion: ship as the secret-tier chat backend in [[tether-brain]] when auditability > raw decode speed.",
  },
  {
    path: "research-vault/experiments/E31-cohort-eval.md",
    front: front("E31 — Cohort precision/recall", "experiment", ["experiment", "veil"], "e31-cohort-eval"),
    body:
      "_2026-04-22._\n\n" +
      "Sweep K=4,8,16,32 over a 1k-doc test vault. For each K, measure: (1) attacker re-identification rate via [[cosine-similarity]] from the LLM's response, (2) downstream answer quality vs raw query.\n\n" +
      "Best knee: K=8. Higher K dilutes answers; lower K leaks identity.\n\n" +
      "Confirms [[E29-veil-cohort]]'s default. Drives the [[ternary-veil]] config.",
  },

  // ── people ──────────────────────────────────────────────────────────
  {
    path: "people/baris.md",
    front: front("Baris Gunaydin", "person", ["person", "self"], "baris", "private"),
    body:
      "Solo researcher. Builds [[tether-brain]], [[zero-tvm]], [[ternary-veil]].\n\n" +
      "Located in Bangkok (Asia/Bangkok, UTC+7).\n\n" +
      "Working through the kernel-set thesis — that hand-written WGSL beats compiler-emitted at smart inflection points.",
  },

  // ── meetings ────────────────────────────────────────────────────────
  {
    path: "meetings/001-kickoff.md",
    front: front("Kickoff — tether-brain v2", "meeting", ["meeting"], "m-001-kickoff"),
    body:
      "_2026-04-25._\n\n" +
      "Decision: rewrite Tether v1 onto Solid + Vite + Bun + Cloudflare. Drop Next.js, drop Tauri, drop Electron Bridge.\n\n" +
      "Architecture: [[tether-brain]] is one URL, zero install, vault as primary surface, chat as side panel, [[ternary-veil]] gating remote calls.\n\n" +
      "Demo backend for secret tier: [[zero-tvm]].\n\n" +
      "Attendees: [[baris]].",
  },
  {
    path: "meetings/002-spawn3-review.md",
    front: front("Spawn 3 — product audit", "meeting", ["meeting", "athena"], "m-002-spawn3"),
    body:
      "_2026-04-26._\n\n" +
      "Athena ran spawn 2 with kernel benches as primary routes — wrong. Refired spawn 3 anchored to the product surface (vault + chat + topbar). Kernel benches demoted to /dev/*.\n\n" +
      "Outcome: [[tether-brain]] is now a real product, not a benchmark gallery. Audit confirmed by clicking through localhost:5173 as a user.\n\n" +
      "Lesson canonized in the Athena pantheon file.",
  },

  // ── daily ───────────────────────────────────────────────────────────
  {
    path: "daily/2026-04-26.md",
    front: front("2026-04-26", "daily", ["daily"], "d-2026-04-26", "private"),
    body:
      "Spawn 3 lands. Audit confirmed. [[tether-brain]] is real.\n\n" +
      "Open: pick the next round. Comms ([[iris]]?), or the brain agent's first real chat through [[ternary-veil]].\n\n" +
      "See [[m-002-spawn3]] for the meeting notes.",
  },

  // ── resources/papers ────────────────────────────────────────────────
  {
    path: "research-vault/resources/papers/k-anon-cohort.md",
    front: front("K-anonymous cohort sampler (paper draft)", "paper", ["paper", "veil", "k-anon"], "k-anon-cohort"),
    body:
      "Title: \"Cohort blending for prompt privacy at K=8.\"\n\n" +
      "Method: see [[E29-veil-cohort]]. Empirical: see [[E31-cohort-eval]].\n\n" +
      "Frames [[ternary-veil]]'s contribution as: a privacy primitive that is (a) deployable on commodity remote LLMs, (b) cheap (single embedding + KNN), (c) verifiable (audit log shows the cohort sent).",
  },
  {
    path: "research-vault/resources/papers/wgsl-compute.md",
    front: front("Hand-written WGSL is enough", "paper", ["paper", "wgsl"], "wgsl-compute"),
    body:
      "Thesis: for decoder-only LLMs of Phi-3 shape, ~80% of the autotuning compiler's complexity budget isn't buying much. Matmul + attention + int4 dequant dominate; everything else is plumbing.\n\n" +
      "[[zero-tvm]] tests it: 10 kernels do what WebLLM does with 85, at 78% the throughput.\n\n" +
      "Closest reference point: Karpathy's llm.c. This work is llm.c ported to browser + WebGPU + int4 + paged-KV + a model people use.",
  },

  // ── resources/concepts ──────────────────────────────────────────────
  {
    path: "research-vault/resources/concepts/cosine-similarity.md",
    front: front("Cosine similarity", "concept", ["concept", "kernels"], "cosine-similarity"),
    body:
      "Dot product over L2-normalized vectors. Dominant similarity primitive for vault search in [[tether-brain]] over [[multilingual-e5-small]] embeddings.\n\n" +
      "Implemented as a hand-written WGSL kernel — see [[wgsl-compute]] for the thesis.",
  },
  {
    path: "research-vault/resources/concepts/bitonic-sort.md",
    front: front("Bitonic top-K", "concept", ["concept", "kernels"], "bitonic-sort"),
    body:
      "Data-parallel partial sort. Workgroup-shared memory variant: 1024 elements per workgroup, sorted descending in 10 stages × ~10 sub-stages.\n\n" +
      "Used as the second kernel in [[tether-brain]]'s search chain after [[cosine-similarity]] — pulls top-K from N=1M sims in <1 ms on M-class GPU.",
  },
  {
    path: "research-vault/resources/concepts/multilingual-e5-small.md",
    front: front("multilingual-e5-small", "concept", ["concept", "embeddings"], "multilingual-e5-small"),
    body:
      "384-dim dense embedding model from intfloat. Multilingual (100+ languages).\n\n" +
      "Quantized to int8 client-side; symmetric per-vector scale = max(|v|)/127.\n\n" +
      "Used everywhere in [[tether-brain]] that needs a vector — [[E29-veil-cohort]] cohort selection, [[cosine-similarity]] search, [[ternary-veil]] tier classifier fallback.",
  },

  // ── resources/tools ─────────────────────────────────────────────────
  {
    path: "research-vault/resources/tools/three-js.md",
    front: front("three.js", "note", ["tool", "graphics"], "three-js"),
    body:
      "WebGL/WebGPU 3D rendering library. v0.171+ has the WebGPURenderer at `three/webgpu` (top-level subpath).\n\n" +
      "Used in [[tether-brain]]'s vault orbit and the [[temple]] embodied chat.\n\n" +
      "Caveat: TSL `storage()` direct-binding to InstancedMesh transform attribute is fragile across encoder boundaries on M-class. Falls back to a 2-deep ring of MAP_READ staging buffers (1–2 frame stale, invisible at 60 Hz).",
  },

  // ── areas (private + secret to demo Veil) ───────────────────────────
  {
    path: "areas/health/sleep.md",
    front: front("Sleep notes", "note", ["health", "private"], "health-sleep", "private"),
    body:
      "Personal — sleep tracking. Demo doc to show [[ternary-veil]]'s private tier in action: any chat that pulls this in cohort-blends it with K-1 decoys before reaching a remote LLM.",
  },
  {
    path: "areas/research/webgpu.md",
    front: front("WebGPU notes", "note", ["research", "wgsl"], "research-webgpu"),
    body:
      "Running notes on WebGPU's shader-i8 feature, atomicAdd<f32> gap, dispatchWorkgroupsIndirect, etc.\n\n" +
      "Feeds [[wgsl-compute]] and [[tether-brain]]'s kernel set.",
  },

  // ── inbox ───────────────────────────────────────────────────────────
  {
    path: "inbox/2026-04-26-from-slack.md",
    front: front("Pasted from Slack", "note", ["inbox"], "inbox-slack-26"),
    body:
      "Snippet pasted from a chat — \"have you tried Solid signals at 60Hz vs React reconciler? curious about the live-pulse story.\"\n\n" +
      "→ relevant to [[tether-brain]]'s side-panel design choice.",
  },

  // ── archives ────────────────────────────────────────────────────────
  {
    path: "archives/old-tether-spec.md",
    front: front("Tether v1 — original spec (archived)", "note", ["archive"], "old-tether-spec"),
    body:
      "Pre-rewrite design doc. Kept for forensic recovery.\n\n" +
      "v2 supersedes — see [[tether-brain]] and [[m-001-kickoff]] for the rewrite decision.",
  },

];

function toIngestResult(seed: DemoSeed): IngestResult {
  return {
    title: seed.front.title ?? seed.path,
    content: seed.body,
    front: seed.front,
    kind: "md",
    path: seed.path,
  };
}

/**
 * Seed the running vault with 20 connected demo docs.
 * Idempotent-ish: skips docs whose path already exists in the store.
 * Returns the count of docs actually added.
 *
 * Pass an explicit `storeOverride` to bind to a specific store instance —
 * useVault holds a captured reference whose subscribers fire on emit; calling
 * `getVaultStore()` again here would resolve through the workspace resolver
 * and might land on a different instance, leaving the UI subscription stale.
 */
export async function seedDemo(storeOverride?: VaultStore): Promise<number> {
  const store = storeOverride ?? getVaultStore();
  await store.init();

  // Build a path → existing-doc map so we can skip dupes.
  const existingPaths = new Set(
    store.signal().map((d) => d.path).filter((p): p is string => Boolean(p)),
  );

  let added = 0;
  for (const seed of DEMO) {
    if (existingPaths.has(seed.path)) continue;
    const doc = await store.addDoc(toIngestResult(seed));
    if (doc) added++;
  }
  return added;
}

export const DEMO_SEED_COUNT = DEMO.length;
